import { Controller } from "@hotwired/stimulus"
import { init } from "echarts"

export default class extends Controller {
  static values = {
    data: { type: Array, default: [] },
    homeColor: { type: String, default: "#059669" },
    awayColor: { type: String, default: "#2563eb" },
  }

  connect() {
    if (this.dataValue.length < 2) return

    this._resizeObserver = new ResizeObserver(() => this._chart?.resize())
    this._resizeObserver.observe(this.element)

    const { minutes, homeXg, awayXg } = this._parseData(this.dataValue)
    if (minutes.length < 2) return

    const halftimeIdx = minutes.findIndex(m => m >= 45)
    const maxXg = Math.max(...homeXg, ...awayXg, 0.01)

    const chart = init(this.element, null, { renderer: "svg" })
    chart.setOption({
      grid: { left: 4, right: 4, top: 4, bottom: 20 },
      xAxis: {
        type: "category",
        data: minutes.map(m => this._fmtMinute(m)),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          fontSize: 9,
          color: "#9ca3af",
          interval: (idx) => {
            const m = minutes[idx]
            return m % 5 === 0 || m === 1
          },
          hideOverlap: true,
        },
        splitLine: { show: false },
      },
      yAxis: {
        type: "value",
        show: true,
        min: 0,
        max: maxXg * 1.15,
        splitLine: { lineStyle: { color: "#374151", type: "dashed", opacity: 0.3 } },
        axisLabel: {
          fontSize: 9,
          color: "#9ca3af",
          formatter: (v) => v.toFixed(1),
        },
      },
      tooltip: {
        trigger: "axis",
        backgroundColor: "rgba(17,24,39,0.9)",
        borderWidth: 0,
        textStyle: { color: "#f3f4f6", fontSize: 12 },
        formatter: (params) => {
          const m = minutes[params[0].dataIndex]
          const h = homeXg[params[0].dataIndex]
          const a = awayXg[params[0].dataIndex]
          return `<div style="display:flex;flex-direction:column;gap:2px">
            <strong>${this._fmtMinute(m)}</strong>
            <span style="display:flex;align-items:center;gap:4px">
              <span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${this.homeColorValue}"></span>
              xG: ${h.toFixed(2)}
            </span>
            <span style="display:flex;align-items:center;gap:4px">
              <span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${this.awayColorValue}"></span>
              xG: ${a.toFixed(2)}
            </span>
          </div>`
        },
      },
      series: [
        {
          name: "Casa",
          type: "line",
          data: homeXg,
          smooth: true,
          symbol: "none",
          lineStyle: { color: this.homeColorValue, width: 2 },
          areaStyle: {
            color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: this.homeColorValue + "40" },
                { offset: 1, color: this.homeColorValue + "05" },
              ] },
          },
          markLine: halftimeIdx >= 0 ? {
            silent: true,
            symbol: "none",
            lineStyle: { color: "#6b7280", type: "dashed", width: 1 },
            label: { show: false },
            data: [{ xAxis: halftimeIdx }],
          } : undefined,
        },
        {
          name: "Visitante",
          type: "line",
          data: awayXg,
          smooth: true,
          symbol: "none",
          lineStyle: { color: this.awayColorValue, width: 2 },
          areaStyle: {
            color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: this.awayColorValue + "40" },
                { offset: 1, color: this.awayColorValue + "05" },
              ] },
          },
        },
      ],
      legend: { show: false },
    })

    this._chart = chart
  }

  disconnect() {
    this._resizeObserver?.disconnect()
    this._chart?.dispose()
  }

  _parseData(raw) {
    const first = raw[0]
    let minutes, homeXg, awayXg

    if (first.minute !== undefined) {
      minutes = raw.map(p => p.minute)
      homeXg = raw.map(p => parseFloat(p.home ?? p.home_xg ?? 0))
      awayXg = raw.map(p => parseFloat(p.away ?? p.away_xg ?? 0))
    } else if (first.m !== undefined) {
      minutes = raw.map(p => p.m)
      homeXg = raw.map(p => parseFloat(p.xg_home ?? p.h ?? p.home ?? 0))
      awayXg = raw.map(p => parseFloat(p.xg_away ?? p.a ?? p.away ?? 0))
    } else if (Array.isArray(raw)) {
      minutes = raw.map((_, i) => i + 1)
      homeXg = raw.map(p => parseFloat(Array.isArray(p) ? p[0] : 0))
      awayXg = raw.map(p => parseFloat(Array.isArray(p) ? p[1] : 0))
    } else {
      return { minutes: [], homeXg: [], awayXg: [] }
    }

    return { minutes, homeXg, awayXg }
  }

  _cumulative(arr) {
    let sum = 0
    return arr.map(v => { sum += v; return parseFloat(sum.toFixed(3)) })
  }

  _fmtMinute(m) {
    if (m === Math.floor(m)) return `${m}'`
    const whole = Math.floor(m)
    const dec = Math.round((m - whole) * 10)
    return `${whole}+${dec}'`
  }
}
